import { type SeamTape, GarmentPieceFabrics } from '@seddi/scene';

import { PropsDiff } from '../core/reconciler/propsDiff';
import {
  type SeamTapeRenderable,
  attachFabric,
  detachFabric,
  updateGrain,
} from '../renderables/seamTapeRenderable';
import { getFabricColorId } from '../resources/fabric';
import type { FabricWithColor } from '../resources/fabricTypes';
import type { SceneReconcilerFrame } from './sceneReconcilerFrame';

/**
 * Reconcile seam tape.
 * @param renderable - Seam tape renderable.
 * @param nextProps - Seam tape prev state.
 * @param prevProps - Seam tape next state.
 * @param frame - Reconciler frame.
 */
export const update = (
  renderable: SeamTapeRenderable,
  nextProps: SeamTape,
  prevProps: SeamTape | undefined,
  frame: SceneReconcilerFrame,
) => {
  const diffProps = PropsDiff.diff(nextProps, prevProps);
  let dirty = false;

  // Update fabric.
  if (diffProps.includes('fabric') || diffProps.includes('fabricColor')) {
    dirty = updateFabric(renderable, nextProps, prevProps, frame) || dirty;
  }

  // Update grain.
  if (diffProps.includes('grain') && renderable.fabricRef) {
    updateGrain(renderable, nextProps.grain);
    dirty = true;
  }

  return dirty;
};

/**
 * Reconcile seam tape fabric.
 * @param renderable - Seam tape renderable.
 * @param nextProps - Seam tape prev state.
 * @param prevProps - Seam tape next state.
 * @param frame - Reconciler frame.
 */
const updateFabric = (
  renderable: SeamTapeRenderable,
  nextProps: SeamTape,
  prevProps: SeamTape | undefined,
  frame: SceneReconcilerFrame,
): boolean => {
  const { resources, nextScene } = frame;

  if (prevProps) {
    detachFabric(renderable, resources);
  }
  if (!nextProps) {
    return true;
  }

  const fabricColorId = getFabricColorId(nextProps.fabric, nextProps.fabricColor);
  let dirty = false;

  if (resources.findFabric(fabricColorId)) {
    attachFabric(renderable, fabricColorId, nextProps.grain, frame.resources);
    dirty = true;
  } else {
    if (!resources.hasFabricRequest(fabricColorId)) {
      const fabric = GarmentPieceFabrics.FindFabricInScene(nextScene, nextProps.fabric);
      const fabricWithColor: FabricWithColor = {
        ...fabric,
        id: fabricColorId,
        recolorTarget: nextProps.fabricColor,
      };
      resources.loadFabricDeferred(fabricWithColor);
    }
  }

  return dirty;
};
